import React, { useState } from 'react';
import { connect } from 'redux-zero/react';
import { formActions } from 'app/state/actions';
import { useTranslation } from 'react-i18next';
import {
  DsExplorerLink,
  DsSplitTag,
  DsSelectSimple,
  DsSelectMultiple,
  DsSelectOption,
  DsInputDatePicker,
  DsInputString,
  DsTag,
  DsNumber,
} from '@tokenyico/design-system/dist/reactJS';

import { datetimeFormat, numberFormat, vEmptyOr, vPattern } from '@tokenyico/design-system';
import { TableView, TransactionTypeCell } from 'app/components';
import { HolderService } from 'services';
import { startCase } from 'lodash';

import TableWrapper from 'app/components/TableWrapper/TableWrapper';

const TransactionsView = ({ setError, user, selectedToken }) => {
  const { t } = useTranslation();
  const userWallet = user.walletsAndStatus[0].wallet;

  const [tableData, setTableData] = useState([]);
  const [tableTotalRows, setTableTotalRows] = useState(0);

  const [uiFilter, setUiFilter] = useState({
    status: null,
    type: [],
    datetimeFrom: null,
    datetimeUntil: null,
    transactionHash: '',
  });

  const header = [
    {
      key: 'status',
      label: t('shared.status'),
      isSortable: false,
    },
    {
      key: 'type',
      label: t('shared.type'),
    },
    {
      key: 'datetime',
      label: t('shared.dateTime'),
    },
    {
      key: 'sender',
      label: t('shared.sender'),
    },
    {
      key: 'recipient',
      label: t('shared.recipient'),
    },
    {
      key: 'amount',
      label: t('shared.amount'),
    },
    {
      key: 'balance',
      label: t('shared.balance'),
      isSortable: false,
    },
    {
      key: 'blockUnblock',
      label: t('shared.blockUnblock'),
      isSortable: false,
    },
    {
      key: 'transactionHash',
      label: t('shared.txHash'),
      isSortable: false,
    },
  ];

  const loadData = async (detail) => {
    const query = {
      limit: detail.rowsPerPage,
      offset: (detail.currentPage - 1) * detail.rowsPerPage,
      filter: detail.filter,
      sortBy: detail.sortBy,
      sortDirection: detail.sortDirection,
    };
    try {
      const data = await HolderService.getTransactions(selectedToken.id, query);
      setTableTotalRows(data.totalItemsFound);
      setTableData(data.items);
    } catch (e) {
      setError(e);
    }
  };
  const onResetFilters = () => {
    setUiFilter({
      status: null,
      type: [],
      datetimeFrom: null,
      datetimeUntil: null,
      transactionHash: '',
    });
  };

  return (
    <TableView id="transactionsView">
      <TableWrapper
        height="100%"
        width="100%"
        header={header}
        data={tableData}
        totalRows={tableTotalRows}
        rowsPerPage={10}
        isSortable
        sortBy="datetime"
        sortDirection="DESC"
        onFetchData={loadData}
        onResetFilters={onResetFilters}
        // rendering functions
        noData={() => (
          <div className="empty-state">
            <span className="text-secondary">{t('shared.noTransactions')}</span>
          </div>
        )}
        status={({ value }) => {
          return value === 'PENDING' ? (
            <DsTag label={t('shared.pending')} version="warning" size="xs" />
          ) : value === 'CONFIRMED' ? (
            <DsTag label={t('shared.confirmed')} version="success" size="xs" />
          ) : value === 'FAILED' ? (
            <DsTag label={t('shared.failed')} version="error" size="xs" />
          ) : (
            ''
          );
        }}
        type={({ value }) => <TransactionTypeCell type={value} />}
        datetime={({ value }) => datetimeFormat(value)}
        sender={({ row, value }) => {
          const isSender = row.sender.wallet.includes(userWallet);
          return isSender ? (
            <div className="_flex:row">
              <DsExplorerLink
                address={value.wallet}
                network={selectedToken.blockchainNetwork}
                type="wallet"
                isTokenTransfer
                width="10rem"
              />
              <span className="_m:top(1) _text:xs _text-grey-900 _m:left(3)">
                ({t('shared.me')})
              </span>
            </div>
          ) : (
            <DsExplorerLink
              address={value.wallet}
              network={selectedToken.blockchainNetwork}
              type="wallet"
              isTokenTransfer
              width="10rem"
              style={{ maxWidth: '14rem' }}
            />
          );
        }}
        recipient={({ row }) => {
          const isSender = row.sender.wallet.includes(userWallet);
          return !isSender ? (
            <div className="_flex:row">
              <DsExplorerLink
                address={row.recipient.wallet}
                network={selectedToken.blockchainNetwork}
                type="wallet"
                isTokenTransfer
                width="10rem"
              />
              <span className="_text:xs _text-grey-900 _m:left(3)">({t('shared.me')})</span>
            </div>
          ) : (
            <DsExplorerLink
              address={row.recipient.wallet}
              network={selectedToken.blockchainNetwork}
              type="wallet"
              isTokenTransfer
              width="10rem"
            />
          );
        }}
        amount={({ value }) => (
          <div className="text-right">{value ? <DsNumber number={value} /> : '-'}</div>
        )}
        balance={({ row }) => {
          const isSender = row.sender.wallet.includes(userWallet);
          return (
            <div className="text-right">
              {row[isSender ? 'sender' : 'recipient']?.balance ? (
                <DsNumber number={row[isSender ? 'sender' : 'recipient'].balance} />
              ) : (
                '-'
              )}
            </div>
          );
        }}
        blockUnblock={({ row }) => {
          const isSender = row.sender.wallet.includes(userWallet);
          return row[isSender ? 'sender' : 'recipient']?.frozenBalance &&
            row[isSender ? 'sender' : 'recipient']?.unfrozenBalance ? (
            <DsSplitTag
              isCopyable
              isFixedWidth
              leftIcon="lock"
              leftValue={numberFormat(
                row[isSender ? 'sender' : 'recipient'].frozenBalance,
                selectedToken.decimals,
              )}
              rightIcon="verified"
              rightValue={numberFormat(
                row[isSender ? 'sender' : 'recipient'].unfrozenBalance,
                selectedToken.decimals,
              )}
            />
          ) : (
            '-'
          );
        }}
        transactionHash={({ value }) => (
          <DsExplorerLink
            address={value}
            network={selectedToken.blockchainNetwork}
            type="transaction"
            isTokenTransfer
            width="10rem"
          />
        )}
      >
        <DsSelectSimple
          isFloatLabel
          isMini
          isClearable
          name="status"
          value={uiFilter.status}
          onDsInput={({ detail }) => setUiFilter({ ...uiFilter, status: detail })}
          label={t('shared.status')}
          options={[
            { value: '', label: t('shared.all') },
            { value: 'FAILED', label: t('shared.failed') },
            { value: 'PENDING', label: t('shared.pending') },
            { value: 'COMPLETED', label: t('shared.confirmed') },
          ]}
          className="_m:right(2)"
          style={{ marginTop: '0px' }}
        />
        <DsSelectMultiple
          isFloatLabel
          isMini
          isClearable
          name="type"
          value={uiFilter.type}
          onDsInput={({ detail }) => setUiFilter({ ...uiFilter, type: detail })}
          label={t('shared.type')}
          className="_m:right(2)"
        >
          <div slot="value">
            <span>{uiFilter.type.map(startCase).join(', ')}</span>
          </div>
          <div slot="options">
            <div>
              <DsSelectOption value="transfer">{t('transactionsFilters.transfer')}</DsSelectOption>
              <DsSelectOption value="mint">{t('transactionsFilters.mint')}</DsSelectOption>
              <DsSelectOption value="burn">{t('transactionsFilters.burn')}</DsSelectOption>
              <DsSelectOption value="frozen">{t('transactionsFilters.block')}</DsSelectOption>
              <DsSelectOption value="unfrozen">{t('transactionsFilters.unblock')}</DsSelectOption>
            </div>
          </div>
        </DsSelectMultiple>
        <DsInputDatePicker
          isFloatLabel
          isMini
          isClearable
          label="From"
          name="datetimeFrom"
          value={
            typeof uiFilter.datetimeFrom === 'string'
              ? uiFilter.datetimeFrom.substring(0, 10)
              : null
          }
          onDsInput={({ detail }) =>
            setUiFilter({
              ...uiFilter,
              datetimeFrom: typeof detail === 'string' ? detail + 'T00:00:00.000Z' : null,
            })
          }
          className="_m:right(2)"
          max={
            typeof uiFilter.datetimeUntil === 'string'
              ? uiFilter.datetimeUntil.substring(0, 10)
              : new Date().toISOString().substring(0, 10)
          }
        />
        <DsInputDatePicker
          isFloatLabel
          isMini
          isClearable
          label="Until"
          name="datetimeUntil"
          value={
            typeof uiFilter.datetimeUntil === 'string'
              ? uiFilter.datetimeUntil.substring(0, 10)
              : null
          }
          onDsInput={({ detail }) =>
            setUiFilter({
              ...uiFilter,
              datetimeUntil: typeof detail === 'string' ? detail + 'T23:59:59.999Z' : null,
            })
          }
          className="_m:right(2)"
          min={
            typeof uiFilter.datetimeFrom === 'string'
              ? uiFilter.datetimeFrom.substring(0, 10)
              : '1999-01-01'
          }
          max={new Date().toISOString().substring(0, 10)}
        />
        <DsInputString
          isFloatLabel
          isMini
          isClearable
          label={t('shared.txHash')}
          name="transactionHash"
          value={uiFilter.transactionHash}
          onDsInput={({ detail }) => setUiFilter({ ...uiFilter, transactionHash: detail })}
          validate={(v) =>
            vEmptyOr(vPattern(/^0x[A-Fa-f0-9]+$/))(v) ? t('validation.transactionHash') : undefined
          }
        />
      </TableWrapper>
    </TableView>
  );
};

const mapToProps = ({ holder: { user, selectedToken } }) => ({ user, selectedToken });
export default connect(mapToProps, formActions)(TransactionsView);
